import React from 'react';
import {
  Box,
  Card,
  Flex,
  hashStringToTagColor,
  LoadingOrbit,
  Progress,
  Skeleton,
  StatusIndicator,
  type StatusIndicatorProps,
  StepProgressBar,
  TagBadge,
  Text,
  TruncatedText,
} from '@chatsdet/ui';
import type { PlannerActivityEvent } from '@chatsdet/types';
import type { ExploratoryTestEnvironmentInstance } from '../types';

type PlannerActivity = PlannerActivityEvent['data'];

/**
 * Planning is stage 1 of the exploratory test lifecycle the user watches:
 * plan → run → report. Surfacing all three orients the wait ("you are here,
 * two stages to go") instead of leaving it feeling stalled.
 */
const PIPELINE_STEPS = [
  { id: 'plan', label: 'Plan' },
  { id: 'run', label: 'Run' },
  { id: 'report', label: 'Report' },
];

const STATUS_VARIANT: Record<PlannerActivity['status'], StatusIndicatorProps['variant']> = {
  running: 'notice',
  completed: 'positive',
  error: 'negative',
};

/**
 * Live feed of the planner's tool calls, newest first. Each row collapses a
 * tool call's running→completed updates (upserted by callId upstream), so the
 * list reads as discrete steps rather than a flood of status churn.
 */
function ActivityFeed({ activity }: { activity: PlannerActivity[] }) {
  const ordered = React.useMemo(() => [...activity].reverse(), [activity]);
  return (
    <Box className="max-h-72 space-y-3 overflow-y-auto pr-1">
      {ordered.map((item) => (
        <Flex key={item.callId} align="start" gap="sm">
          <StatusIndicator
            variant={STATUS_VARIANT[item.status]}
            size="sm"
            pulse={item.status === 'running'}
            className="mt-1 shrink-0"
            aria-hidden
          />
          <Box className="min-w-0 flex-1">
            <Text size="sm">{item.label}</Text>
            {item.detail ? (
              <TruncatedText
                text={item.detail}
                className="text-xs font-mono text-muted-foreground"
              />
            ) : null}
          </Box>
        </Flex>
      ))}
    </Box>
  );
}

const PLANNER_BUDGET_MS = 15 * 60 * 1000;
const PLANNER_BUDGET_MIN = Math.round(PLANNER_BUDGET_MS / 60000);

function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms)) return '--:--';
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Isolated 1Hz clock — only this child re-renders each second, leaving the
 * Activity and Sandbox cards stable. It owns both the elapsed progress bar and
 * the human-readable timer copy so nothing above it needs to tick.
 */
function ElapsedClock({ startMs }: { startMs: number | null }) {
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (startMs === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startMs]);

  const elapsedMs = startMs === null ? null : now - startMs;
  const overBudget = elapsedMs !== null && elapsedMs > PLANNER_BUDGET_MS;
  const ratio =
    elapsedMs === null ? 0 : Math.min(1, Math.max(0, elapsedMs / PLANNER_BUDGET_MS));

  return (
    <Box className="w-full space-y-1.5">
      <Progress
        value={ratio}
        size="sm"
        variant={overBudget ? 'warning' : 'default'}
        aria-label="Planning progress"
      />
      {startMs === null ? (
        <Text size="sm" className="text-muted-foreground">
          Starting up…
        </Text>
      ) : overBudget ? (
        <Text size="sm" className="text-muted-foreground">
          Running long — the planner usually finishes within about {PLANNER_BUDGET_MIN}{' '}
          minutes.
        </Text>
      ) : (
        <Text size="sm" className="text-muted-foreground">
          {formatElapsed(elapsedMs ?? NaN)} elapsed · about {PLANNER_BUDGET_MIN} min max
        </Text>
      )}
    </Box>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text
      size="xs"
      weight="semibold"
      className="text-muted-foreground uppercase tracking-wide"
    >
      {children}
    </Text>
  );
}

/**
 * Faint graph-paper grid behind the panel — adds depth without competing for
 * attention. Masked to fade at the edges so it reads as texture, not a table.
 */
function GridBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          'linear-gradient(to right, rgba(100,116,139,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(100,116,139,0.08) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        maskImage: 'radial-gradient(ellipse at center, black 35%, transparent 85%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 35%, transparent 85%)',
      }}
    />
  );
}

type PlanningViewProps = {
  planningStartedAt: string | null | undefined;
  environmentInstance: ExploratoryTestEnvironmentInstance | null;
  /** Live planner tool-call feed (empty until the first event streams in) */
  activity?: PlannerActivity[];
};

/**
 * Rendered while an exploratory test is in the `planning` lifecycle state — the
 * on-sandbox planner agent is researching code, staging data, and authoring
 * the test plan. This can stay on screen for several minutes, so the view is
 * built to feel oriented and alive: a plan→run→report pipeline shows where you
 * are, an elapsed progress bar softens the wait, and the planner's live
 * `planner.activity` tool-call feed is promoted to the hero. Until the first
 * event arrives we fall back to a static description. When the row transitions
 * to `running`, the page swaps in the tester view.
 */
export function PlanningView({
  planningStartedAt,
  environmentInstance,
  activity = [],
}: PlanningViewProps) {
  const parsed = planningStartedAt ? Date.parse(planningStartedAt) : NaN;
  const startMs = Number.isFinite(parsed) ? parsed : null;
  const hasActivity = activity.length > 0;
  const credential = environmentInstance?.seededCredential ?? null;

  return (
    <Box className="relative h-full w-full overflow-y-auto">
      <GridBackdrop />

      <Flex
        direction="column"
        gap="lg"
        align="stretch"
        className="relative z-10 mx-auto w-full max-w-2xl px-6 py-10"
      >
        <StepProgressBar steps={PIPELINE_STEPS} currentStep={0} className="mb-1" />

        <Card variant="bordered" padding="md">
          <Flex align="center" gap="md">
            <LoadingOrbit size="sm" />
            <Box className="min-w-0 flex-1">
              <Text size="lg" weight="semibold">
                Planning your test…
              </Text>
            </Box>
          </Flex>
          <ElapsedClock startMs={startMs} />
        </Card>

        <Card variant="bordered" padding="md">
          <Flex align="center" justify="between">
            <SectionLabel>Activity</SectionLabel>
            {hasActivity ? (
              <Flex align="center" gap="xs">
                <StatusIndicator variant="info" size="sm" pulse aria-hidden />
                <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
                  Live
                </Text>
              </Flex>
            ) : null}
          </Flex>
          {hasActivity ? (
            <ActivityFeed activity={activity} />
          ) : (
            <Text size="sm" className="text-muted-foreground">
              The planner is reviewing your codebase, staging realistic test data, and
              authoring a test plan. Once it&apos;s done, the browser tester will run the
              plan and you&apos;ll see live screenshots here.
            </Text>
          )}
        </Card>

        <Card variant="bordered" padding="md">
          <SectionLabel>Sandbox</SectionLabel>
          <Box className="space-y-2">
            {environmentInstance ? (
              <>
                <Flex align="center" gap="sm">
                  <Text size="xs" className="w-16 shrink-0 text-muted-foreground">
                    URL
                  </Text>
                  {environmentInstance.publicUrl ? (
                    <TruncatedText
                      text={environmentInstance.publicUrl}
                      className="text-sm font-mono"
                    />
                  ) : (
                    <StatusIndicator variant="notice" size="sm" pulse>
                      <Text as="span" size="sm" className="text-muted-foreground">
                        provisioning…
                      </Text>
                    </StatusIndicator>
                  )}
                </Flex>
                <Flex align="center" gap="sm">
                  <Text size="xs" className="w-16 shrink-0 text-muted-foreground">
                    Login
                  </Text>
                  {credential ? (
                    <Flex align="center" gap="sm" wrap>
                      <TagBadge
                        name={credential.name}
                        color={hashStringToTagColor(credential.role ?? credential.name)}
                        size="sm"
                      />
                      {credential.role ? (
                        <Text size="xs" className="text-muted-foreground">
                          {credential.role}
                        </Text>
                      ) : null}
                    </Flex>
                  ) : (
                    <StatusIndicator variant="notice" size="sm" pulse>
                      <Text as="span" size="sm" className="text-muted-foreground">
                        seeding…
                      </Text>
                    </StatusIndicator>
                  )}
                </Flex>
              </>
            ) : (
              <>
                <Skeleton variant="text" className="w-full" />
                <Skeleton variant="text" className="w-2/3" />
              </>
            )}
          </Box>
        </Card>
      </Flex>
    </Box>
  );
}
